import { useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { Alert, Box, Button } from '@mui/material';
import {
  DefinitionList,
  DetailDrawer,
  DetailDrawerBody,
  FeedbackSnackbar,
  FormDialog,
  FormTextField,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  useFeedbackSnackbar,
  type ResourceCollectionState,
} from '../../../design-system';
import { certificateRequests } from '../fixtures/certificateFixtures';
import {
  certificateStatusLabel,
  certificateStatusTone,
  displayCertificateDate,
} from '../model/presentation';
import type { CertificateAccessProps, CertificateRequest } from '../model/types';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import type { CertificateOperationsPort } from '../port/CertificatesPort';

export function CertificateOperationsPage({
  port,
  fixtureMode,
  readOnly,
  scopes,
}: {
  port: CertificateOperationsPort;
  fixtureMode: boolean;
} & CertificateAccessProps) {
  const canRevoke = !readOnly && scopes.includes('certificates.revoke');
  const { notify, feedbackProps } = useFeedbackSnackbar();
  const resource = useAsyncResource(
    () => fixtureMode ? Promise.resolve(certificateRequests) : port.listCertificateOperations(),
    [port, fixtureMode],
  );
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<CertificateRequest>();
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const rows = (resource.data ?? []).filter((item) => `${item.requesterName} ${item.displayName} ${item.destination ?? ''}`.toLowerCase().includes(query.toLowerCase()) && (!status || item.status === status));
  const columns: GridColDef<CertificateRequest>[] = [
    { field: 'requesterName', headerName: '신청자', width: 140 },
    { field: 'displayName', headerName: '증명서', flex: 1, minWidth: 160 },
    { field: 'destination', headerName: '제출처', flex: 1, minWidth: 150, valueFormatter: (value) => value || '-' },
    { field: 'requestedAt', headerName: '신청일', width: 130, valueFormatter: (value) => displayCertificateDate(String(value)) },
    { field: 'status', headerName: '상태', width: 120, renderCell: ({ value }) => <StatusBadge tone={certificateStatusTone(value)} label={certificateStatusLabel(value)} /> },
  ];

  const revoke = () => {
    if (!canRevoke || !selected?.issuance || !reason.trim()) return;
    setSubmitting(true);
    setError(undefined);
    const promise: Promise<CertificateRequest> = fixtureMode
      ? Promise.resolve({
        ...selected,
        issuance: { ...selected.issuance, status: 'REVOKED', revokedAt: new Date().toISOString() },
      })
      : port.revokeCertificate(selected.issuance.id, reason);
    void promise.then((item) => {
      setSelected(item);
      setRevokeOpen(false);
      setReason('');
      notify('발급본을 폐기했습니다.', { tone: 'success' });
      void resource.reload();
    }, (cause: unknown) => {
      setError(cause instanceof Error ? cause.message : '발급본을 폐기하지 못했습니다.');
    }).finally(() => setSubmitting(false));
  };

  const collectionState: ResourceCollectionState = resource.loading
    ? { kind: 'loading' }
    : resource.error
      ? { kind: 'error', message: resource.error.message, onRetry: resource.reload }
      : rows.length === 0
        ? { kind: 'empty', title: resource.data?.length ? '검색 조건에 맞는 증명서가 없습니다.' : '처리할 증명서가 없습니다.' }
        : { kind: 'ready' };

  return <>
    <FeedbackSnackbar {...feedbackProps} />
    <ResourcePage title="증명서" help="증명서 내용은 원장 정보를 정정한 뒤 새로 발급합니다.">
      <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {error && !revokeOpen && <Alert severity="error" sx={{ mb: 1.5, flexShrink: 0 }}>{error}</Alert>}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResourceCollection
            state={collectionState}
            rows={rows}
            totalCount={(resource.data ?? []).length}
            columns={columns}
            query={query}
            onQueryChange={setQuery}
            searchPlaceholder="신청자·증명서·제출처 검색"
            ariaLabel="증명서 운영 목록"
            filters={[{ label: '상태', value: status, onChange: setStatus, options: [{ label: '전체', value: '' }, { label: '발급 완료', value: 'ISSUED' }, { label: '확인 필요', value: 'FAILED' }, { label: '처리 중', value: 'REQUESTED' }] }]}
            onRowClick={({ row }) => setSelected(row)}
          />
        </Box>
      </Box>
    </ResourcePage>
    <DetailDrawer open={Boolean(selected)} onClose={() => setSelected(undefined)} title={selected?.displayName ?? ''} meta={selected?.requesterName} footer={canRevoke && selected?.issuance?.status === 'VALID' ? <Button color="error" variant="outlined" onClick={() => { if (!canRevoke) return; setError(undefined); setRevokeOpen(true); }}>발급본 폐기</Button> : undefined}>
      {selected && <DetailDrawerBody spacing={2}><DefinitionList items={[["신청자", selected.requesterName || '-'], ['제출처', selected.destination || '-'], ['상태', <StatusBadge label={certificateStatusLabel(selected.status)} tone={certificateStatusTone(selected.status)} />], ['신청일', displayCertificateDate(selected.requestedAt)], ['발급번호', selected.issuance?.issuanceNumber || '-'], ['검증 상태', selected.issuance ? <StatusBadge label={certificateStatusLabel(selected.issuance.status)} tone={certificateStatusTone(selected.issuance.status)} /> : '-']]} />{selected.errorSummary && <Alert severity="warning">{selected.errorSummary}</Alert>}</DetailDrawerBody>}
    </DetailDrawer>
    {canRevoke && <FormDialog
      open={revokeOpen}
      onClose={() => setRevokeOpen(false)}
      onSubmit={revoke}
      title="발급본 폐기"
      cancelLabel="취소"
      submitLabel="폐기 확정"
      busy={submitting}
      busyLabel="처리 중…"
      submitDisabled={!reason.trim()}
      submitColor="error"
      maxWidth="xs"
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Alert severity="warning">PDF는 감사 증적으로 보존되지만 외부 진위확인은 즉시 ‘폐기’로 표시됩니다.</Alert>
      <FormTextField label="폐기 사유" value={reason} onChange={(event) => setReason(event.target.value)} multiline minRows={3} required />
    </FormDialog>}
  </>;
}
